import { MAX_PROP_DIVISIONS, type PropBounds } from './propDrawingPrimitives';
import { townColorHex as hex } from './townPropTemplates';
import type { TownPropPalette } from './townPropTemplates';

export interface FishingNetOptions { readonly columns?: number; readonly rows?: number }
export interface RopeCoilOptions { readonly turns?: number }

const count = (value: number) => Number.isSafeInteger(value) && value >= 1 && value <= MAX_PROP_DIVISIONS;
function usableBounds(b: PropBounds): boolean {
  return !!b && typeof b === 'object'
    && [b.x, b.y, b.width, b.height, b.x + b.width, b.y + b.height].every(Number.isFinite)
    && b.width > 0 && b.height > 0 && b.x + b.width > b.x && b.y + b.height > b.y;
}
function usablePalette(p: TownPropPalette): boolean {
  return !!p && ['shade', 'woodEdge', 'stone'].every(key => {
    const color = p[key as keyof TownPropPalette];
    return Number.isInteger(color) && color >= 0 && color <= 0xffffff;
  });
}
function usableOptions(value: unknown, keys: readonly string[]): boolean {
  return !!value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).every(key => keys.includes(key));
}
function ropePath(d: string, thickness: number, p: TownPropPalette): string {
  return `<path d="${d}" fill="none" stroke="${hex(p.shade)}" stroke-width="${thickness}" stroke-linecap="round" stroke-linejoin="round" />
    <path d="${d}" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="${thickness * .62}" stroke-linecap="round" stroke-linejoin="round" />`;
}

/** Rectangular cross-lattice net with diamond openings. Perimeter, strands and
 * knots are all inset inside bounds; no backing fills the mesh openings.
 * Work is bounded by at most 64×64 cells plus their interior junctions. */
export function drawFishingNet(b: PropBounds, p: TownPropPalette, options: FishingNetOptions = {}): string {
  if (!usableBounds(b) || !usablePalette(p) || !usableOptions(options, ['columns', 'rows'])) return '';
  const columns = options.columns === undefined ? 6 : options.columns;
  const rows = options.rows === undefined ? 4 : options.rows;
  if (!count(columns) || !count(rows)) return '';
  const thickness = Math.min(b.width / (columns + 1), b.height / (rows + 1)) * .12;
  const inset = thickness * 1.5, left = b.x + inset, top = b.y + inset;
  const width = b.width - inset * 2, height = b.height - inset * 2;
  if (!(thickness > 0 && width > 0 && height > 0)) return '';
  const cellW = width / columns, cellH = height / rows, lines: string[] = [], knots: string[] = [];
  const knot = (x: number, y: number) => `<circle data-net-knot="crossing" cx="${x}" cy="${y}" r="${thickness * .7}"
    fill="${hex(p.woodEdge)}" stroke="${hex(p.shade)}" stroke-width="${thickness * .3}" />
    <circle cx="${x - thickness * .13}" cy="${y - thickness * .13}" r="${thickness * .22}" fill="${hex(p.stone)}" />`;
  for (let row = 0; row < rows; row++) for (let column = 0; column < columns; column++) {
    const x = left + column * cellW, y = top + row * cellH;
    lines.push(`M${x} ${y} L${x + cellW} ${y + cellH} M${x + cellW} ${y} L${x} ${y + cellH}`);
    knots.push(knot(x + cellW / 2, y + cellH / 2));
    if (row > 0 && column > 0) knots.push(knot(x, y));
  }
  const perimeter = `M${left} ${top} H${left + width} V${top + height} H${left}Z`;
  return `<g data-prop-part="fishing-net">
    <g data-net-part="mesh">${ropePath(lines.join(' '), thickness, p)}</g>
    <g data-net-part="perimeter">${ropePath(perimeter, thickness * 1.6, p)}</g>
    <g data-net-part="knots">${knots.join('')}</g>
  </g>`;
}

/** Nested elliptical windings with an interior rope end. The bounds contain
 * every stroke and round cap; the center and spaces between turns stay open.
 * Turns are bounded 1..64 and scale with the requested frame. */
export function drawRopeCoil(b: PropBounds, p: TownPropPalette, options: RopeCoilOptions = {}): string {
  if (!usableBounds(b) || !usablePalette(p) || !usableOptions(options, ['turns'])) return '';
  const turns = options.turns === undefined ? 4 : options.turns;
  if (!count(turns)) return '';
  const thickness = Math.min(b.width, b.height) / (turns * 2 + 4) * .8;
  const cx = b.x + b.width * .46, cy = b.y + b.height * .43;
  const outerX = b.width * .44 - thickness, outerY = b.height * .38 - thickness;
  if (!(thickness > 0 && outerX > 0 && outerY > 0)) return '';
  const parts = ['<g data-prop-part="rope-coil">'];
  for (let index = 0; index < turns; index++) {
    const fraction = (turns - index) / turns, rx = outerX * fraction, ry = outerY * fraction;
    parts.push(`<g data-rope-winding="${index}" fill="none">
      <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" stroke="${hex(p.shade)}" stroke-width="${thickness}" />
      <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" stroke="${hex(p.woodEdge)}" stroke-width="${thickness * .65}" />
      <path d="M${cx - rx} ${cy} A${rx} ${ry} 0 0 1 ${cx + rx} ${cy}"
        stroke="${hex(p.stone)}" stroke-width="${thickness * .22}" stroke-linecap="round" />
    </g>`);
  }
  const end = `M${cx + outerX / turns} ${cy} Q${b.x + b.width * .65} ${b.y + b.height * .87}
    ${b.x + b.width * .82} ${b.y + b.height * .87} L${b.x + b.width - thickness} ${b.y + b.height - thickness}`;
  parts.push(`<g data-rope-part="terminal">${ropePath(end, thickness, p)}</g></g>`);
  return parts.join('');
}
